import { useState, useMemo } from 'react';
import { useFormacao, type FormacaoParticipante } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import RegistarParticipacaoModal from './components/RegistarParticipacaoModal';
import AvaliarParticipacaoModal from './components/AvaliarParticipacaoModal';
import EditarParticipacaoModal from './components/EditarParticipacaoModal';

type PartSortKey = 'nome' | 'formacao' | 'horas' | 'estado' | 'nota' | 'avaliacao';
type PartSortDir = 'asc' | 'desc';
type ViewMode = 'lista' | 'formandos';

const ESTADO_PART_ORDER: Record<string, number> = {
  'A Frequentar': 0, 'Inscrito': 1, 'Concluído': 2, 'Reprovado': 3, 'Desistiu': 4,
};

const estadoCfg: Record<string, { bg: string; text: string; dot: string }> = {
  'Inscrito':       { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
  'A Frequentar':   { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  'Concluído':      { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Desistiu':       { bg: 'bg-rose-50',    text: 'text-rose-600',    dot: 'bg-rose-400'    },
  'Reprovado':      { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400'  },
};

const avatarPalette = [
  { bg: 'bg-rose-100', text: 'text-rose-600' },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100', text: 'text-amber-600' },
  { bg: 'bg-teal-100', text: 'text-teal-600' },
  { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function getAvatarPalette(id: string) {
  const nums = id.replace(/\D/g, '');
  const idx = nums ? parseInt(nums, 10) % avatarPalette.length : 0;
  return avatarPalette[isNaN(idx) ? 0 : idx];
}

function getInitials(nome: string) {
  return nome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

type EnrichedParticipante = FormacaoParticipante & {
  nomeFormacao: string;
  horasFormacao: number;
  cargoFunc: string | null;
  unidadeFunc: string | null;
};

interface FormandoResumo {
  funcionario_id: string;
  nome: string;
  carreira: string | null;
  unidade: string | null;
  totalHoras: number;
  totalFormacoes: number;
  concluidas: number;
  certificados: number;
  mediaAvaliacao: number | null;
  participacoes: EnrichedParticipante[];
  ultimaFormacao: string | null;
}

// Limiar de horas para indicador visual (meta legal 35h)
const META_HORAS = 35;

function HorasIndicador({ horas, meta = META_HORAS }: { horas: number; meta?: number }) {
  const pct = Math.min(Math.round((horas / meta) * 100), 100);
  const color = pct >= 100 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
  const bgColor = pct >= 100 ? 'bg-emerald-50' : pct >= 60 ? 'bg-amber-50' : 'bg-rose-50';
  const textColor = pct >= 100 ? 'text-emerald-700' : pct >= 60 ? 'text-amber-700' : 'text-rose-700';
  const label = pct >= 100 ? 'Meta atingida' : pct >= 60 ? 'Em progresso' : 'Abaixo da meta';

  return (
    <div className={`rounded-lg px-3 py-2 ${bgColor} flex items-center gap-3`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-[11px] font-bold ${textColor}`}>{horas}h</span>
          <span className="text-[10px] text-zinc-400">/{meta}h</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/70 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: color }}
          />
        </div>
      </div>
      <span className={`text-[10px] font-semibold ${textColor} whitespace-nowrap`}>{label}</span>
    </div>
  );
}

export default function ParticipantesFormacaoPage() {
  const { participantes, formacoes, loading, saving, removerParticipante, getDocumentoUrl } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [search, setSearch] = useState('');
  const [formacaoFilter, setFormacaoFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('lista');
  const [showModal, setShowModal] = useState(false);
  const [confirmRemover, setConfirmRemover] = useState<string | null>(null);
  const [avaliarParticipante, setAvaliarParticipante] = useState<EnrichedParticipante | null>(null);
  const [editarParticipante, setEditarParticipante] = useState<EnrichedParticipante | null>(null);
  const [sortKey, setSortKey] = useState<PartSortKey>('nome');
  const [sortDir, setSortDir] = useState<PartSortDir>('asc');
  const [expandedFormando, setExpandedFormando] = useState<string | null>(null);
  const [formandoSort, setFormandoSort] = useState<'horas' | 'nome' | 'formacoes'>('horas');

  // Enriquecer participantes com dados da formação
  const enriched = useMemo((): EnrichedParticipante[] => {
    return participantes.map((p) => {
      const formacao = formacoes.find((f) => f.id === p.formacao_id);
      const func = funcionarios.find((f) => f.id === p.funcionario_id);
      return {
        ...p,
        nomeFormacao: formacao?.nome ?? 'Formação não encontrada',
        horasFormacao: formacao?.duracao_horas ?? 0,
        cargoFunc: func?.carreira ?? null,
        unidadeFunc: func?.unidadeOrganica ?? null,
      };
    });
  }, [participantes, formacoes, funcionarios]);

  // ── Vista por formando ─────────────────────────────────────────────────────
  const formandosResumo = useMemo((): FormandoResumo[] => {
    const map = new Map<string, FormandoResumo>();

    enriched.forEach((p) => {
      const existing = map.get(p.funcionario_id);
      const formacao = formacoes.find((f) => f.id === p.formacao_id);
      const dataRef = formacao?.data_fim ?? formacao?.data_inicio ?? null;

      if (!existing) {
        map.set(p.funcionario_id, {
          funcionario_id: p.funcionario_id,
          nome: p.funcionario_nome ?? p.funcionario_id,
          carreira: p.cargoFunc,
          unidade: p.unidadeFunc,
          totalHoras: p.horasFormacao,
          totalFormacoes: 1,
          concluidas: p.estado_participacao === 'Concluído' ? 1 : 0,
          certificados: p.certificado_emitido ? 1 : 0,
          mediaAvaliacao: p.avaliacao_nota !== null ? p.avaliacao_nota : null,
          participacoes: [p],
          ultimaFormacao: dataRef,
        });
      } else {
        existing.totalHoras += p.horasFormacao;
        existing.totalFormacoes += 1;
        if (p.estado_participacao === 'Concluído') existing.concluidas += 1;
        if (p.certificado_emitido) existing.certificados += 1;
        if (p.avaliacao_nota !== null) {
          existing.mediaAvaliacao = existing.mediaAvaliacao !== null
            ? (existing.mediaAvaliacao + p.avaliacao_nota) / 2
            : p.avaliacao_nota;
        }
        existing.participacoes.push(p);
        if (dataRef && (!existing.ultimaFormacao || dataRef > existing.ultimaFormacao)) {
          existing.ultimaFormacao = dataRef;
        }
      }
    });

    const list = Array.from(map.values());

    // Ordenar
    list.sort((a, b) => {
      if (formandoSort === 'horas') return b.totalHoras - a.totalHoras;
      if (formandoSort === 'formacoes') return b.totalFormacoes - a.totalFormacoes;
      return a.nome.localeCompare(b.nome);
    });

    return list;
  }, [enriched, formacoes, formandoSort]);

  // Estatísticas globais dos formandos
  const formandosStats = useMemo(() => {
    const total = formandosResumo.length;
    const atingiramMeta = formandosResumo.filter((f) => f.totalHoras >= META_HORAS).length;
    const semFormacao = funcionarios.length - total;
    const mediaHoras = total > 0
      ? Math.round(formandosResumo.reduce((s, f) => s + f.totalHoras, 0) / total)
      : 0;
    const maxHoras = total > 0 ? Math.max(...formandosResumo.map((f) => f.totalHoras)) : 0;
    return { total, atingiramMeta, semFormacao, mediaHoras, maxHoras };
  }, [formandosResumo, funcionarios]);

  function handleSort(key: PartSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const filtered = useMemo(() => {
    const base = enriched.filter((p) => {
      const nome = p.funcionario_nome ?? '';
      const matchSearch = search === '' ||
        nome.toLowerCase().includes(search.toLowerCase()) ||
        p.funcionario_id.includes(search);
      const matchFormacao = formacaoFilter === '' || p.formacao_id === formacaoFilter;
      const matchEstado = estadoFilter === '' || p.estado_participacao === estadoFilter;
      return matchSearch && matchFormacao && matchEstado;
    });

    return [...base].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'nome': cmp = (a.funcionario_nome ?? '').localeCompare(b.funcionario_nome ?? ''); break;
        case 'formacao': cmp = a.nomeFormacao.localeCompare(b.nomeFormacao); break;
        case 'horas': cmp = a.horasFormacao - b.horasFormacao; break;
        case 'estado': cmp = (ESTADO_PART_ORDER[a.estado_participacao] ?? 99) - (ESTADO_PART_ORDER[b.estado_participacao] ?? 99); break;
        case 'nota': cmp = (a.nota_final ?? -1) - (b.nota_final ?? -1); break;
        case 'avaliacao': cmp = (a.avaliacao_nota ?? -1) - (b.avaliacao_nota ?? -1); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [enriched, search, formacaoFilter, estadoFilter, sortKey, sortDir]);

  const filteredFormandos = useMemo(() => {
    if (!search) return formandosResumo;
    return formandosResumo.filter((f) =>
      f.nome.toLowerCase().includes(search.toLowerCase()) ||
      (f.carreira ?? '').toLowerCase().includes(search.toLowerCase())
    );
  }, [formandosResumo, search]);

  const stats = useMemo(() => {
    const totalHoras = filtered.reduce((s, p) => s + p.horasFormacao, 0);
    const certs = filtered.filter((p) => p.certificado_emitido).length;
    const concluidos = filtered.filter((p) => p.estado_participacao === 'Concluído').length;
    const avaliados = filtered.filter((p) => p.avaliacao_nota !== null).length;
    return { total: filtered.length, totalHoras, certs, concluidos, avaliados };
  }, [filtered]);

  const partHeaders: { key: PartSortKey | null; label: string; center?: boolean }[] = [
    { key: 'nome', label: 'Funcionário' },
    { key: 'formacao', label: 'Ação de Formação' },
    { key: 'horas', label: 'Horas', center: true },
    { key: 'estado', label: 'Estado' },
    { key: 'nota', label: 'Nota' },
    { key: 'avaliacao', label: 'Avaliação' },
    { key: null, label: 'Certif.', center: true },
    { key: null, label: '' },
  ];

  async function handleRemover(id: string) {
    await removerParticipante(id);
    setConfirmRemover(null);
  }

  function renderStars(nota: number | null) {
    if (nota === null) return <span className="text-[12px] text-zinc-300">—</span>;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
            {nota >= s
              ? <i className="ri-star-fill text-xs text-amber-400" />
              : <i className="ri-star-line text-xs text-zinc-200" />
            }
          </div>
        ))}
        <span className="text-[11px] font-semibold text-zinc-500 ml-1">{nota}/5</span>
      </div>
    );
  }

  return (
    <>
      <main className="flex-1 bg-slate-50/60">
        {/* Header */}
        <div className="bg-white border-b border-zinc-100 px-8 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-user-star-line text-white text-lg" />
              </div>
              <div>
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Participantes</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">Registo de participações em ações de formação por funcionário</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Toggle vista */}
              <div className="flex items-center bg-zinc-100 rounded-lg p-0.5 gap-0.5">
                <button
                  onClick={() => setViewMode('lista')}
                  className={`whitespace-nowrap px-3.5 py-1.5 rounded-md text-[12px] font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${viewMode === 'lista' ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-list-check text-xs" /></div>
                  Lista
                </button>
                <button
                  onClick={() => setViewMode('formandos')}
                  className={`whitespace-nowrap px-3.5 py-1.5 rounded-md text-[12px] font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${viewMode === 'formandos' ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-bar-chart-horizontal-line text-xs" /></div>
                  Por Formando
                </button>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
                Registar Participação
              </button>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-5">
          {/* Stats */}
          {!loading && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { label: 'Participações',     value: stats.total,            icon: 'ri-group-line',    bg: 'bg-zinc-100 text-zinc-600'      },
                { label: 'Horas de Formação', value: `${stats.totalHoras}h`, icon: 'ri-time-line',     bg: 'bg-zinc-100 text-zinc-600'      },
                { label: 'Concluídas',        value: stats.concluidos,       icon: 'ri-check-line',    bg: 'bg-emerald-50 text-emerald-600' },
                { label: 'Certificados',      value: stats.certs,            icon: 'ri-medal-line',    bg: 'bg-amber-50 text-amber-600'     },
                { label: 'Avaliadas',         value: stats.avaliados,        icon: 'ri-star-line',     bg: 'bg-amber-50 text-amber-600'     },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                    <i className={`${s.icon} text-base`} />
                  </div>
                  <div>
                    <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                    <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                <i className="ri-search-line text-sm" />
              </div>
              <input
                type="text"
                placeholder="Pesquisar por nome ou número do funcionário..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
              />
            </div>
            {viewMode === 'lista' && (
              <>
                <select
                  value={formacaoFilter}
                  onChange={(e) => setFormacaoFilter(e.target.value)}
                  className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer max-w-[220px]"
                >
                  <option value="">Todas as formações</option>
                  {formacoes.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
                <select
                  value={estadoFilter}
                  onChange={(e) => setEstadoFilter(e.target.value)}
                  className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
                >
                  <option value="">Todos os estados</option>
                  {Object.keys(estadoCfg).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </>
            )}
            {viewMode === 'formandos' && (
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] text-zinc-400">Ordenar:</span>
                {(['horas', 'formacoes', 'nome'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setFormandoSort(s)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${formandoSort === s ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}`}
                  >
                    {s === 'horas' ? 'Mais horas' : s === 'formacoes' ? 'Mais formações' : 'Nome'}
                  </button>
                ))}
              </div>
            )}
            <span className="text-[12px] text-zinc-400 ml-auto">
              {viewMode === 'lista'
                ? <><span className="font-semibold text-zinc-600">{filtered.length}</span> participação(ões)</>
                : <><span className="font-semibold text-zinc-600">{filteredFormandos.length}</span> formando(s)</>
              }
            </span>
          </div>

          {/* ── Vista por Formando ─────────────────────────────────────────── */}
          {viewMode === 'formandos' && !loading && (
            <>
              {/* Painel de estatísticas dos formandos */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    icon: 'ri-user-line',
                    value: formandosStats.total,
                    label: 'Formandos com registo',
                    bg: 'bg-zinc-100 text-zinc-600',
                  },
                  {
                    icon: 'ri-trophy-line',
                    value: `${formandosStats.atingiramMeta}`,
                    label: `Atingiram ${META_HORAS}h (meta legal)`,
                    bg: 'bg-emerald-50 text-emerald-600',
                    sub: `${formandosStats.total > 0 ? Math.round((formandosStats.atingiramMeta / formandosStats.total) * 100) : 0}% do total`,
                  },
                  {
                    icon: 'ri-time-line',
                    value: `${formandosStats.mediaHoras}h`,
                    label: 'Média de horas por formando',
                    bg: 'bg-zinc-100 text-zinc-600',
                  },
                  {
                    icon: 'ri-user-unfollow-line',
                    value: formandosStats.semFormacao,
                    label: 'Funcionários sem formação',
                    bg: formandosStats.semFormacao > 0 ? 'bg-rose-50 text-rose-600' : 'bg-zinc-100 text-zinc-600',
                  },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                      <i className={`${s.icon} text-base`} />
                    </div>
                    <div>
                      <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                      <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                      {'sub' in s && s.sub && <p className="text-[10px] text-zinc-400">{s.sub}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Legenda da barra */}
              <div className="flex items-center gap-4 px-1">
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Indicador de horas (meta: {META_HORAS}h)</span>
                <div className="flex items-center gap-3">
                  {[
                    { color: 'bg-emerald-500', label: `≥${META_HORAS}h — Meta atingida` },
                    { color: 'bg-amber-500', label: `≥${Math.round(META_HORAS * 0.6)}h — Em progresso` },
                    { color: 'bg-rose-500', label: `<${Math.round(META_HORAS * 0.6)}h — Abaixo da meta` },
                  ].map((l) => (
                    <div key={l.label} className="flex items-center gap-1.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                      <span className="text-[11px] text-zinc-500">{l.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lista de formandos */}
              {filteredFormandos.length === 0 ? (
                <div className="bg-white rounded-xl border border-zinc-100 py-16 flex flex-col items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-user-star-line text-4xl text-zinc-200" />
                  </div>
                  <p className="text-[14px] font-semibold text-zinc-400">Nenhum formando encontrado</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredFormandos.map((formando) => {
                    const palette = getAvatarPalette(formando.funcionario_id);
                    const isExp = expandedFormando === formando.funcionario_id;
                    const pct = Math.min(Math.round((formando.totalHoras / META_HORAS) * 100), 100);
                    const barColor = pct >= 100 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
                    const statusBg = pct >= 100 ? 'border-emerald-200' : pct >= 60 ? 'border-amber-200' : 'border-rose-200';

                    return (
                      <div key={formando.funcionario_id} className={`bg-white rounded-xl border overflow-hidden ${statusBg}`}>
                        <div
                          className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-zinc-50/50 transition-colors"
                          onClick={() => setExpandedFormando(isExp ? null : formando.funcionario_id)}
                        >
                          {/* Avatar */}
                          <div className={`w-10 h-10 flex items-center justify-center rounded-full text-[12px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                            {getInitials(formando.nome)}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-[14px] font-bold text-zinc-900">{formando.nome}</p>
                              {formando.carreira && (
                                <span className="text-[10px] font-medium bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full">
                                  {formando.carreira}
                                </span>
                              )}
                            </div>
                            {formando.unidade && (
                              <p className="text-[11px] text-zinc-400 mt-0.5">{formando.unidade}</p>
                            )}
                          </div>

                          {/* Barra de horas */}
                          <div className="w-48 flex-shrink-0 hidden lg:block">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[11px] font-bold text-zinc-700">{formando.totalHoras}h</span>
                              <span className="text-[10px] text-zinc-400">{pct}% da meta</span>
                            </div>
                            <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{ width: `${pct}%`, backgroundColor: barColor }}
                              />
                            </div>
                          </div>

                          {/* Métricas */}
                          <div className="flex items-center gap-5 flex-shrink-0">
                            <div className="text-center">
                              <p className="text-[16px] font-black text-zinc-800">{formando.totalFormacoes}</p>
                              <p className="text-[10px] text-zinc-400">formações</p>
                            </div>
                            <div className="text-center">
                              <p className="text-[16px] font-black text-emerald-600">{formando.concluidas}</p>
                              <p className="text-[10px] text-zinc-400">concluídas</p>
                            </div>
                            <div className="text-center">
                              <p className="text-[16px] font-black text-amber-600">{formando.certificados}</p>
                              <p className="text-[10px] text-zinc-400">certificados</p>
                            </div>
                            {formando.mediaAvaliacao !== null && (
                              <div className="text-center">
                                <p className="text-[16px] font-black text-zinc-700">{formando.mediaAvaliacao.toFixed(1)}</p>
                                <p className="text-[10px] text-zinc-400">avaliação</p>
                              </div>
                            )}
                          </div>

                          {/* Expand */}
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                            <i className={`ri-arrow-down-s-line text-zinc-400 text-lg transition-transform ${isExp ? 'rotate-180' : ''}`} />
                          </div>
                        </div>

                        {/* Detalhe expandido */}
                        {isExp && (
                          <div className="border-t border-zinc-100 bg-zinc-50/50 px-5 py-4">
                            {/* Indicador visual detalhado */}
                            <div className="mb-4">
                              <HorasIndicador horas={formando.totalHoras} meta={META_HORAS} />
                            </div>
                            <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wide mb-3">
                              Histórico de participações
                            </p>
                            <div className="space-y-2">
                              {formando.participacoes.map((p) => {
                                const ec = estadoCfg[p.estado_participacao] ?? estadoCfg['Inscrito'];
                                return (
                                  <div key={p.id} className="bg-white rounded-lg border border-zinc-100 px-4 py-3 flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[13px] font-semibold text-zinc-800 truncate">{p.nomeFormacao}</p>
                                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${ec.bg} ${ec.text}`}>
                                          <span className={`w-1.5 h-1.5 rounded-full ${ec.dot}`} />
                                          {p.estado_participacao}
                                        </span>
                                        {p.nota_final && (
                                          <span className="text-[10px] text-zinc-500">Nota: {p.nota_final}</span>
                                        )}
                                        {p.certificado_emitido && (
                                          <span className="text-[10px] font-semibold text-emerald-600 flex items-center gap-0.5">
                                            <i className="ri-medal-line text-xs" /> Certificado
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <p className="text-[14px] font-black text-zinc-700">{p.horasFormacao}h</p>
                                      {p.avaliacao_nota !== null && (
                                        <div className="flex items-center gap-0.5 justify-end mt-0.5">
                                          {[1,2,3,4,5].map((s) => (
                                            <div key={s} className="w-3 h-3 flex items-center justify-center">
                                              <i className={`${p.avaliacao_nota! >= s ? 'ri-star-fill text-amber-400' : 'ri-star-line text-zinc-200'} text-[10px]`} />
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {/* Total acumulado */}
                            <div className="mt-3 flex items-center justify-end gap-2 border-t border-zinc-100 pt-3">
                              <span className="text-[12px] text-zinc-500">Total acumulado:</span>
                              <span className="text-[15px] font-black text-zinc-900">{formando.totalHoras}h</span>
                              <span className="text-[11px] text-zinc-400">em {formando.totalFormacoes} formação(ões)</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── Vista Lista ────────────────────────────────────────────────── */}
          {viewMode === 'lista' && (
            <>
              {loading ? (
                <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
                  </div>
                  <p className="text-[13px] text-zinc-400">A carregar participantes...</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-user-star-line text-4xl text-zinc-200" />
                  </div>
                  <p className="text-[14px] font-semibold text-zinc-400">
                    {participantes.length === 0 ? 'Ainda não há participações registadas' : 'Nenhuma participação encontrada'}
                  </p>
                  {participantes.length === 0 && formacoes.length > 0 && (
                    <button
                      onClick={() => setShowModal(true)}
                      className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
                      Registar primeira participação
                    </button>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-100 bg-zinc-50">
                        {partHeaders.map((col, ci) => (
                          <th
                            key={ci}
                            onClick={() => col.key && handleSort(col.key)}
                            className={`px-5 py-3 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap select-none group ${
                              col.center ? 'text-center' : 'text-left'
                            } ${
                              col.key ? 'cursor-pointer hover:bg-zinc-100 transition-colors' : ''
                            } ${
                              sortKey === col.key ? 'text-zinc-700' : 'text-zinc-400'
                            }`}
                          >
                            <span className={`inline-flex items-center gap-1 ${col.center ? 'justify-center' : ''}`}>
                              {col.label}
                              {col.key && (
                                sortKey === col.key
                                  ? <i className={`${sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'} text-[13px] text-zinc-700`} />
                                  : <i className="ri-expand-up-down-line text-[11px] text-zinc-300 group-hover:text-zinc-400" />
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p, i) => {
                        const nome = p.funcionario_nome ?? p.funcionario_id;
                        const palette = getAvatarPalette(p.funcionario_id);
                        const ec = estadoCfg[p.estado_participacao] ?? estadoCfg['Inscrito'];
                        return (
                          <tr key={p.id} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 flex items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                                  {getInitials(nome)}
                                </div>
                                <div>
                                  <p className="text-[13px] font-semibold text-zinc-800">{nome}</p>
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-[11px] text-zinc-400 font-mono">#{p.funcionario_id}</p>
                                    {p.unidadeFunc && (
                                      <span className="text-[10px] text-zinc-400">· {p.unidadeFunc}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <p className="text-[12px] font-medium text-zinc-700 max-w-[200px] truncate">{p.nomeFormacao}</p>
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              <span className="text-[13px] font-bold text-zinc-700">{p.horasFormacao}h</span>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${ec.bg} ${ec.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${ec.dot} flex-shrink-0`} />
                                {p.estado_participacao}
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <span className="text-[12px] text-zinc-600">{p.nota_final ?? '—'}</span>
                            </td>
                            <td className="px-5 py-3.5">
                              {renderStars(p.avaliacao_nota)}
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              {p.certificado_url ? (
                                <a
                                  href={getDocumentoUrl(p.certificado_url)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Descarregar certificado"
                                  className="whitespace-nowrap w-7 h-7 flex items-center justify-center mx-auto rounded-lg text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer"
                                >
                                  <i className="ri-download-line text-sm" />
                                </a>
                              ) : p.certificado_emitido ? (
                                <div className="w-6 h-6 flex items-center justify-center mx-auto" title="Certificado emitido (sem ficheiro)">
                                  <i className="ri-medal-line text-base text-emerald-400" />
                                </div>
                              ) : (
                                <span className="text-[12px] text-zinc-300">—</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setEditarParticipante(p)}
                                  title="Editar participação / gerir certificado"
                                  className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
                                >
                                  <i className="ri-edit-line text-sm" />
                                </button>
                                <button
                                  onClick={() => setAvaliarParticipante(p)}
                                  title={p.avaliacao_nota !== null ? 'Editar avaliação' : 'Avaliar participação'}
                                  className={`whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                    p.avaliacao_nota !== null
                                      ? 'text-amber-400 hover:text-amber-600 hover:bg-amber-50'
                                      : 'text-zinc-300 hover:text-amber-500 hover:bg-amber-50'
                                  }`}
                                >
                                  <i className="ri-star-line text-sm" />
                                </button>
                                {confirmRemover === p.id ? (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleRemover(p.id)}
                                      disabled={saving}
                                      className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 hover:bg-rose-600 px-2.5 py-1 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                                    >
                                      Sim
                                    </button>
                                    <button
                                      onClick={() => setConfirmRemover(null)}
                                      className="whitespace-nowrap text-[11px] font-medium text-zinc-500 bg-zinc-100 px-2.5 py-1 rounded-lg cursor-pointer"
                                    >
                                      Não
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setConfirmRemover(p.id)}
                                    className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-delete-bin-line text-sm" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {showModal && (
        <RegistarParticipacaoModal
          onClose={() => setShowModal(false)}
          onSuccess={() => setShowModal(false)}
        />
      )}

      {avaliarParticipante && (
        <AvaliarParticipacaoModal
          participante={avaliarParticipante}
          onClose={() => setAvaliarParticipante(null)}
          onSuccess={() => setAvaliarParticipante(null)}
        />
      )}

      {editarParticipante && (
        <EditarParticipacaoModal
          participante={editarParticipante}
          onClose={() => setEditarParticipante(null)}
          onSuccess={() => setEditarParticipante(null)}
        />
      )}
    </>
  );
}
